import { useEffect, useState } from 'react';

import { type Environment, fetchQuery, graphql } from 'react-relay';

import type { useUnitLinkedResourceIdsQuery } from './__generated__/useUnitLinkedResourceIdsQuery.graphql';

export interface UnitLinkedResourceIdsOptions {
	/**
	 * ATIs (Atlassian Type Identifiers) to restrict the returned links to, e.g.
	 * `['ati:cloud:atlassian-studio:workspace']`.
	 */
	atis?: string[];
}

export interface UnitLinkedResourceIdsResult {
	error: boolean;
	/** All linked-resource ARIs returned for the given anchor resource's unit. */
	linkedResourceIds: string[];
	loading: boolean;
}

/**
 * Fetches the set of resource ARIs linked to the given anchor resource's unit
 * (collaboration context) via the `admin_unitLinkedResource` GraphQL query.
 *
 * The query is server-side permission filtered by CCM, so only links the caller is
 * permitted to see are returned.
 *
 * @param anchorResourceAri - The resource ARI to resolve linked unit resources for.
 * When empty, the hook skips the fetch and settles to an empty result.
 * @param options - Optional caller-supplied refinements. See
 * {@link UnitLinkedResourceIdsOptions}. Reserved options (e.g. `atis`) are
 * accepted now to stabilise the contract but are applied in a follow-up change.
 */
export const useUnitLinkedResourceIds = (
	relayEnvironment: Environment,
	anchorResourceAri: string,
	options?: UnitLinkedResourceIdsOptions,
): UnitLinkedResourceIdsResult => {
	void options;

	const [state, setState] = useState<UnitLinkedResourceIdsResult>({
		linkedResourceIds: [],
		loading: true,
		error: false,
	});

	useEffect(() => {
		if (!anchorResourceAri) {
			setState({ linkedResourceIds: [], loading: false, error: false });
			return;
		}

		// A local closure flag (rather than a shared ref) ensures a stale in-flight
		// fetch resolving after a new effect run cannot overwrite state for a newer
		// anchor resource ARI.
		let cancelled = false;
		setState({ linkedResourceIds: [], loading: true, error: false });

		fetchQuery<useUnitLinkedResourceIdsQuery>(
			relayEnvironment,
			graphql`
				query useUnitLinkedResourceIdsQuery($id: ID!) {
					admin_unitLinkedResource(id: $id) {
						id
						links {
							edges {
								node {
									id
								}
							}
						}
					}
				}
			`,
			{ id: anchorResourceAri },
			{ fetchPolicy: 'store-or-network' },
		)
			.toPromise()
			.then((result) => {
				if (cancelled) {
					return;
				}
				const edges = result?.admin_unitLinkedResource?.links?.edges ?? [];
				const ids = edges
					.map((edge) => edge?.node?.id)
					.filter((id): id is string => typeof id === 'string');

				setState({ linkedResourceIds: ids, loading: false, error: false });
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				setState({ linkedResourceIds: [], loading: false, error: true });
			});

		return () => {
			cancelled = true;
		};
	}, [anchorResourceAri, relayEnvironment]);

	return state;
};
